import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import { LangContext } from "../../contexts/LangContext";
import { apiService } from "../../services/api";
import { getCookie, setCookie } from "../../services/cookie";
import { useToasts } from "../../hooks/useToasts";
import { MainLayout } from "../Layout/MainLayout";
import { Sidebar } from "../Layout/Sidebar";
import { PageHeader } from "../Layout/PageHeader";
import { TasksList } from "../Tasks/TasksList";
import { TaskSelectionPage } from "../Tasks/TaskSelectionPage";
import { CreateTaskPage } from "../Tasks/CreateTaskPage";
import { BalancePage } from "../Balance/BalancePage";
import { VisualizationPage } from "../Visualization/VisualizationPage";
import { SupportModal } from "../Common/SupportModal";
import { ToastCenter } from "../Common/ToastCenter";
import { ReferralsPage } from "../Referrals/ReferralsPage";
import { VerifyEmailPage } from "../Auth/VerifyEmailPage";
// В импорты добавить:
import { ReelsResultsPage } from "../Tasks/ReelsResultsPage";


import { ResetPasswordPage } from "../Auth/ResetPasswordPage";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

import { OutreachDashboard } from "../Outreach/OutreachDashboard";


export function Cabinet({ userEmail, onLogout }) {
  const { lang } = React.useContext(LangContext);
  const t = lang === "pl" ? pl : en;
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [balance, setBalance] = useState({ scraping_balance: 0, ai_balance: 0 });
  const [loading, setLoading] = useState(true);
  const [showSupport, setShowSupport] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [visualizationConfig, setVisualizationConfig] = useState(null);

  const { toasts, add, remove } = useToasts();
  const token = () => getCookie("authToken");

  const ADMIN_IDS = [13];
  const userInfoObj = JSON.parse(getCookie('userInfo') || '{}');
  const isAdmin = ADMIN_IDS.includes(userInfoObj?.id);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [user, tasksData, balanceData] = await Promise.all([
        apiService.getMe(token()),
        //apiService.getTasks(token()),
        //apiService.getBalance(token()),
      ]);
      setUserInfo(user);
      console.log('userInfo ', user)
      if (user) {
        setCookie('userInfo', JSON.stringify(user), 1);
      }
    } catch (err) {
      add(err.message, "error");
      if (err.message === "Unauthorized") onLogout();
    } finally {
      setLoading(false);
    }
  };

  // Теперь это просто обёртки над navigate
  const handleVisualizeFromTasks = (task) => {
    setVisualizationConfig({
      taskId: task.task_id || task.id,
      fullTask: task,
      autoStart: true,
      mode: "all",
    });
    navigate("/visualization");
  };

  const handleCreateTask = async (taskData) => {
    try {
      window.location.href = "/tasks";
    } catch (err) {
      add(err.message, "error");
    }
  };

  const handleStartTask = async (taskId) => {
    try {
      await apiService.startTask(token(), taskId);
      add(t.taskStarted, "success");
      await loadData();
    } catch (err) {
      add(err.message, "error");
    }
  };

  const handleStopTask = async (taskId) => {
    try {
      await apiService.stopTask(token(), taskId);
      add(t.taskStopped, "success");
      await loadData();
    } catch (err) {
      add(err.message, "error");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      await apiService.deleteTask(token(), taskId);
      add(t.taskDeleted, "info");
      await loadData();
    } catch (err) {
      add(err.message, "error");
    }
  };

  const handleForceStopTopUp = (tokenType, shortage) => {
    console.log('Top up:', tokenType, shortage);
    navigate("/balance");
  };

  const handleUseTemplate = (templateId) => {
    setTemplateId(templateId);
    navigate("/create");
  };

  const handleTopUp = async (type, amount) => {
    try {
      const result = await apiService.topUpBalance(token(), type, amount);
      setBalance(result);
      add(t.purchaseSuccess, "success");
    } catch (err) {
      add(err.message, "error");
    }
  };

  const handleCustomTopUp = async (scraping, ai) => {
    try {
      const result = await apiService.customTopUp(token(), scraping, ai);
      setBalance(result);
      add(t.purchaseSuccess, "success");
    } catch (err) {
      add(err.message, "error");
    }
  };

  const handleSupport = async (msg) => {
    if (!msg.trim()) {
      add(t.supportEnterMsg, "error");
      return;
    }
    try {
      await apiService.support(token(), msg);
      add(t.supportSent, "success");
      setShowSupport(false);
    } catch (err) {
      add(err.message, "error");
    }
  };

  const handleResendVerify = async () => {
    try {
      await apiService.resendVerification(token());
      add(t.verifySent, "success");
    } catch (err) {
      add(err.message, "error");
    }
  };

  const handleTestPrompt = (config) => {
    setVisualizationConfig({
      ...config,
      autoStart: true,
    });
    navigate("/visualization");
  };

  // Определяем текущую страницу для Sidebar и PageHeader
  const getCurrentPage = () => {
    const path = window.location.pathname;
    if (path.includes("/create")) return "create";
    if (path.includes("/select-create")) return "select-create";
    if (path.includes("/balance")) return "balance";
    if (path.includes("/visualization")) return "visualization";
    if (path.includes("/referrals")) return "referrals";
    if (path.includes("/outreach")) return "outreach";

    return "tasks";
  };

  if (loading) {
    return (
      <div className="spinner-page">
        <div className="spinner" />
      </div>
    );
  }

  const currentPage = getCurrentPage();
  const displayEmail = userInfo?.email || userEmail;
  const showVerify = userInfo?.email_verified === false;

  return (
    <>
      <ToastCenter toasts={toasts} onRemove={remove} />
      {showSupport && (
        <SupportModal
          onClose={() => setShowSupport(false)}
          onSend={handleSupport}
        />
      )}

      <MainLayout>
        <Sidebar
          page={currentPage}
          setPage={(page) => {
            const routes = {
              tasks: "/tasks",
              "select-create": "/select-create",
              create: "/create",
              balance: "/balance",
              visualization: "/visualization",
              referrals: "/referrals",
            };
            navigate(routes[page] || "/tasks");
          }}
          userEmail={displayEmail}
          balance={balance}
          onLogout={onLogout}
          onShowSupport={() => setShowSupport(true)}
        />

        <div className="main-content">
          <PageHeader
            page={currentPage}
            scraping_tokens={userInfo.scraping_tokens}
            ai_tokens={userInfo.ai_tokens}
            onShowSupport={() => setShowSupport(true)}
          />

          <div className="page-body">
            <Routes>
               
              <Route path="/reels-results/:taskId" element={<ReelsResultsPage />} />

              <Route path="/" element={
                <TasksList
                  tasks={tasks}
                  showVerify={showVerify}
                  userInfo={userInfo}
                  onAdd={(prefillData) => {
  if (prefillData?.prefillTask) {
    navigate("/create", { state: { prefillTask: prefillData.prefillTask } });
  } else {
    navigate("/select-create");
  }
}}
                  onStart={handleStartTask}
                  onStop={handleStopTask}
                  onDelete={handleDeleteTask}
                  onResendVerify={handleResendVerify}
                  onForceStopTopUp={handleForceStopTopUp}
                  onVisualize={handleVisualizeFromTasks}
                />
              } />
              
              <Route path="/tasks" element={
                <TasksList
                  tasks={tasks}
                  showVerify={showVerify}
                  userInfo={userInfo}
                  onAdd={(prefillData) => {
  if (prefillData?.prefillTask) {
    navigate("/create", { state: { prefillTask: prefillData.prefillTask } });
  } else {
    navigate("/select-create");
  }
}}
                  onStart={handleStartTask}
                  onStop={handleStopTask}
                  onDelete={handleDeleteTask}
                  onResendVerify={handleResendVerify}
                  onForceStopTopUp={handleForceStopTopUp}
                  onVisualize={handleVisualizeFromTasks}
                />
              } />
              
              <Route path="/select-create" element={
                <TaskSelectionPage
                  onCreateFromScratch={() => {
                    setTemplateId(null);
                    sessionStorage.removeItem("create_task_name");
                    sessionStorage.removeItem("create_task_type");
                    sessionStorage.removeItem("create_scraping_data");
                    sessionStorage.removeItem("create_filtering_data");
                    navigate("/create");
                  }}
                  onUseTemplate={handleUseTemplate}
                  onCancel={() => navigate("/tasks")}
                />
              } />
              
              <Route path="/create" element={
                <CreateTaskPage
                  userInfo={userInfo}
                  onSave={handleCreateTask}
                  onCancel={() => navigate("/select-create")}
                  onTestPrompt={handleTestPrompt}
                  initialTemplateId={templateId}
                />
              } />
              
              <Route path="/balance" element={
                <BalancePage
                  userInfo={userInfo}
                  onTopUp={handleTopUp}
                  onCustomTopUp={handleCustomTopUp}
                />
              } />
              
              <Route path="/visualization" element={
                <VisualizationPage
                  tasks={tasks}
                  token={token()}
                  initialConfig={visualizationConfig}
                  onBack={() => {
                    setVisualizationConfig(null);
                    navigate("/tasks");
                  }}
                />
              } />
              
              <Route path="/referrals" element={
                <ReferralsPage />
              } />
              
              <Route path="*" element={
                <TasksList
                  tasks={tasks}
                  showVerify={showVerify}
                  userInfo={userInfo}
                  onAdd={(prefillData) => {
  if (prefillData?.prefillTask) {
    navigate("/create", { state: { prefillTask: prefillData.prefillTask } });
  } else {
    navigate("/select-create");
  }
}}
                  onStart={handleStartTask}
                  onStop={handleStopTask}
                  onDelete={handleDeleteTask}
                  onResendVerify={handleResendVerify}
                  onForceStopTopUp={handleForceStopTopUp}
                  onVisualize={handleVisualizeFromTasks}
                />
              } />

              
             {isAdmin && <Route path="/outreach" element={<OutreachDashboard />} />}
                
            </Routes>
          </div>
        </div>
      </MainLayout>
    </>
  );
}